import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, AlertTriangle, User, Calendar, Clock, ArrowLeft } from 'lucide-react';
import { idCardsAPI, filesAPI } from '../services/api';

const VerifyIDCard = () => {
  const { cardId } = useParams();
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyCard = async () => {
      try {
        const response = await idCardsAPI.verifyCard(cardId);
        setVerificationResult(response.data);
      } catch (error) {
        setVerificationResult({
          valid: false,
          message: 'Unable to verify ID card. Please try again.'
        });
      } finally {
        setLoading(false);
      }
    };

    if (cardId) {
      verifyCard();
    }
  }, [cardId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a365d] to-[#2d4a7c] flex items-center justify-center p-4">
        <div className="text-center text-white">
          <Shield className="w-16 h-16 mx-auto mb-4 animate-pulse" />
          <p className="text-xl">Verifying ID Card...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a365d] to-[#2d4a7c] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 text-white mb-2">
            <Shield className="w-8 h-8" />
            <h1 className="text-2xl font-bold">NC Help Hub</h1>
          </div>
          <p className="text-white/70">Employee ID Verification</p>
        </div>

        {/* Verification Result Card */}
        <div className={`bg-[var(--background-paper)] rounded-2xl shadow-2xl overflow-hidden ${
          verificationResult?.valid ? 'ring-4 ring-green-400' : 'ring-4 ring-red-400'
        }`}>
          {/* Status Banner */}
          <div className={`p-6 text-center ${
            verificationResult?.valid 
              ? 'bg-gradient-to-r from-green-500 to-green-600' 
              : 'bg-gradient-to-r from-red-500 to-red-600'
          }`}>
            {verificationResult?.valid ? (
              <>
                <CheckCircle className="w-16 h-16 mx-auto text-white mb-3" />
                <h2 className="text-2xl font-bold text-white">VERIFIED</h2>
                <p className="text-white/90">{verificationResult.message}</p>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 mx-auto text-white mb-3" />
                <h2 className="text-2xl font-bold text-white">NOT VALID</h2>
                <p className="text-white/90">{verificationResult?.message}</p>
              </>
            )}
          </div>

          {/* Employee Details (only if valid) */}
          {verificationResult?.valid && (
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                {verificationResult.worker_picture ? (
                  <img 
                    src={filesAPI.getUrl(verificationResult.worker_picture)}
                    alt={verificationResult.worker_name}
                    className="w-20 h-20 rounded-xl object-cover border-2 border-[#1a365d]"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-[var(--background-subtle)] flex items-center justify-center">
                    <User className="w-10 h-10 text-gray-400" />
                  </div>
                )}
                <div>
                  <h3 className="text-xl font-bold text-[var(--text-primary)]">{verificationResult.worker_name}</h3>
                  <p className="text-2xl font-mono font-bold text-[#1a365d]">{verificationResult.employee_id}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 text-[var(--text-secondary)]">
                  <Shield className="w-5 h-5 text-[#4CAF50]" />
                  <span className="font-medium">Services:</span>
                  <span>{verificationResult.service_types?.join(', ')}</span>
                </div>
                
                <div className="flex items-center gap-3 text-[var(--text-secondary)]">
                  <Calendar className="w-5 h-5 text-[#D4AF37]" />
                  <span className="font-medium">Issued:</span>
                  <span>{new Date(verificationResult.issued_date).toLocaleDateString()}</span>
                </div>
                
                <div className="flex items-center gap-3 text-[var(--text-secondary)]">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <span className="font-medium">Expires:</span>
                  <span>{new Date(verificationResult.expiration_date).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-[var(--success)]/10 rounded-xl border border-[var(--success)]/30">
                <p className="text-[var(--success)] text-sm text-center flex items-center justify-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  This employee is authorized to provide NC Help Hub services
                </p>
              </div>
            </div>
          )}

          {/* Warning for invalid cards */}
          {!verificationResult?.valid && verificationResult?.employee_id && (
            <div className="p-6">
              <div className="p-4 bg-[var(--error)]/10 rounded-xl border border-[var(--error)]/30">
                <p className="text-[var(--error)] text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span>
                    <strong>Employee ID:</strong> {verificationResult.employee_id}
                    {verificationResult.worker_name && ` (${verificationResult.worker_name})`}
                  </span>
                </p>
                <p className="text-red-700 text-sm mt-2">
                  This ID card is not currently valid. Please contact NC Help Hub for verification.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Back Link */}
        <div className="mt-6 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to NC Help Hub
          </Link>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-white/50 text-sm">
          <p>Service Area: Tarboro, NC 27886</p>
          <p>& cities within 40 miles</p>
        </div>
      </div>
    </div>
  );
};

export default VerifyIDCard;
